/**
 * SSR context: provides host, baseUrl, pathname, and search when rendering on the server.
 * Used by LandingPage for session_expired and Get started link without relying on window.
 */
import React from 'react'

export interface SSRContextValue {
  host: string
  baseUrl: string
  pathname?: string
  search?: string
  webPortalUrl?: string
}

const defaultValue: SSRContextValue = { host: '', baseUrl: '' }

export const SSRContext = React.createContext<SSRContextValue>(defaultValue)

export const SSRContextProvider: React.FC<{
  host: string
  baseUrl: string
  pathname?: string
  search?: string
  webPortalUrl?: string
  children: React.ReactNode
}> = ({ host, baseUrl, pathname, search, webPortalUrl, children }) => (
  <SSRContext.Provider value={{ host, baseUrl, pathname, search, webPortalUrl }}>{children}</SSRContext.Provider>
)

export function useSSRBaseUrl(): string {
  const ctx = React.useContext(SSRContext)
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin
  }
  return ctx?.baseUrl || ''
}

export function useSSRHost(): string {
  const ctx = React.useContext(SSRContext)
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname
  }
  return ctx?.host || ''
}

export function useSSRPathname(): string {
  const ctx = React.useContext(SSRContext)
  if (typeof window !== 'undefined' && window.location?.pathname) {
    return window.location.pathname
  }
  return ctx?.pathname || ''
}

export function useSSRSearch(): string {
  const ctx = React.useContext(SSRContext)
  if (typeof window !== 'undefined' && window.location?.search) {
    return window.location.search
  }
  return ctx?.search || ''
}

export function useSSRWebPortalUrl(): string {
  const ctx = React.useContext(SSRContext)
  return ctx?.webPortalUrl || ''
}
